import { Action, Reducer, ActionCreator, combineReducers } from 'redux';
import { ThunkAction } from 'redux-thunk';

import { RootState } from '..';
import { IEciImplantDetailsState } from '../../interfaces/states/IEciImplantDetailsState';
import { IErrors } from '../../common/model/errors.model';

export enum Type {
  SET_SELECTIONNUMBER = '@@eciImplantDetails/SET_SELECTIONNUMBER',
  SET_SEASON = '@@eciImplantDetails/SET_SEASON',
  SET_SUPPLIERCODE = '@@eciImplantDetails/SET_SUPPLIERCODE',
  SET_REFERENCE = '@@eciImplantDetails/SET_REFERENCE',
  SET_STYLE = '@@eciImplantDetails/SET_STYLE',
  SET_SPINNERDOWNLOADECIFORECAST = '@@eciImplantDetails/SET_SPINNERDOWNLOADECIFORECAST',
  SET_SPINNERDOWNLOADFILLEDECIAPPFORM = '@@eciImplantDetails/SET_SPINNERDOWNLOADFILLEDECIAPPFORM',
  SET_SPINNERUPLOADFILLEDECIAPPFORM = '@@eciImplantDetails/SET_SPINNERUPLOADFILLEDECIAPPFORM',
  SET_ERRORS = '@@eciImplantDetails/SET_ERRORS',
}

const initialState: IEciImplantDetailsState = {
  selectionNumber: null,
  season: null,
  supplierCode: null,
  reference: null,
  style: null,
  errors: {},
  downloadEciForecastFileSpinner: false,
  downloadFileSpinner: false,
  uploadFileSpinner: false
};

// Actions

export type Actions = SetSelectionNumberAction | SetSeasonAction | SetSupplierCodeAction | SetReferenceAction | SetStyleAction
  | SetSpinnerDownloadEciForecastAction
  | SetSpinnerDownloadFilledEciAppFormAction | SetSpinnerUploadFilledEciAppFormAction | SetErrorsAction;

export interface SetSelectionNumberAction extends Action<Type.SET_SELECTIONNUMBER> {
  selectionNumber: string;
}

export interface SetSeasonAction extends Action<Type.SET_SEASON> {
  season: string;
}

export interface SetSupplierCodeAction extends Action<Type.SET_SUPPLIERCODE> {
  supplierCode: string;
}

export interface SetReferenceAction extends Action<Type.SET_REFERENCE> {
  reference: string;
}

export interface SetStyleAction extends Action<Type.SET_STYLE> {
  style: string;
}

export interface SetSpinnerDownloadEciForecastAction extends Action<Type.SET_SPINNERDOWNLOADECIFORECAST> {
  showSpinner: boolean;
}

export interface SetSpinnerDownloadFilledEciAppFormAction extends Action<Type.SET_SPINNERDOWNLOADFILLEDECIAPPFORM> {
  showSpinner: boolean;
}

export interface SetSpinnerUploadFilledEciAppFormAction extends Action<Type.SET_SPINNERUPLOADFILLEDECIAPPFORM> {
  showSpinner: boolean;
}

export interface SetErrorsAction extends Action<Type.SET_ERRORS> {
  errors: IErrors;
}

export const setSelectionNumber: ActionCreator<SetSelectionNumberAction> = (selectionNumber: string) => ({
  type: Type.SET_SELECTIONNUMBER,
  selectionNumber: selectionNumber
});

export const setSeason: ActionCreator<SetSeasonAction> = (season: string) => ({
  type: Type.SET_SEASON,
  season: season
});

export const setSupplierCode: ActionCreator<SetSupplierCodeAction> = (supplierCode: string) => ({
  type: Type.SET_SUPPLIERCODE,
  supplierCode: supplierCode
});

export const setReference: ActionCreator<SetReferenceAction> = (reference: string) => ({
  type: Type.SET_REFERENCE,
  reference: reference
});

export const setStyle: ActionCreator<SetStyleAction> = (style: string) => ({
  type: Type.SET_STYLE,
  style: style
});

export const setSpinnerDownloadEciForecast: ActionCreator<SetSpinnerDownloadEciForecastAction> = (showSpinner: boolean) => ({
  type: Type.SET_SPINNERDOWNLOADECIFORECAST,
  showSpinner: showSpinner
});

export const setSpinnerDownloadFilledEciAppForm: ActionCreator<SetSpinnerDownloadFilledEciAppFormAction> = (showSpinner: boolean) => ({
  type: Type.SET_SPINNERDOWNLOADFILLEDECIAPPFORM,
  showSpinner: showSpinner
});

export const setSpinnerUploadFilledEciAppForm: ActionCreator<SetSpinnerUploadFilledEciAppFormAction> = (showSpinner: boolean) => ({
  type: Type.SET_SPINNERUPLOADFILLEDECIAPPFORM,
  showSpinner: showSpinner
});

export const setErrors: ActionCreator<SetErrorsAction> = (errors: IErrors) => ({
  type: Type.SET_ERRORS,
  errors: errors
});

export const setSelectionNumberInState: ActionCreator<ThunkAction<void, RootState, unknown, Actions>> = (
  selectionNumber: string
) => (
  async (dispatch) => {
    dispatch(setSelectionNumber(selectionNumber));
  }
);

export const setSeasonInState: ActionCreator<ThunkAction<void, RootState, unknown, Actions>> = (
  season: string
) => (
  async (dispatch) => {
    dispatch(setSeason(season));
  }
);

export const setSupplierCodeInState: ActionCreator<ThunkAction<void, RootState, unknown, Actions>> = (
  supplierCode: string
) => (
  async (dispatch) => {
    dispatch(setSupplierCode(supplierCode));
  }
);

export const setReferenceInState: ActionCreator<ThunkAction<void, RootState, unknown, Actions>> = (
  reference: string
) => (
  async (dispatch) => {
    dispatch(setReference(reference));
  }
);

export const setStyleInState: ActionCreator<ThunkAction<void, RootState, unknown, Actions>> = (
  style: string
) => (
  async (dispatch) => {
    dispatch(setStyle(style));
  }
);

export const setErrorsInState: ActionCreator<ThunkAction<void, RootState, unknown, Actions>> = (
  errors: IErrors
) => (
  async (dispatch) => {
    dispatch(setErrors(errors));
  }
);

export const spinnerDownloadEciForecast: ActionCreator<ThunkAction<void, RootState, unknown, Actions>> = (
  showSpinner: boolean
) => (
  async (dispatch) => {
    dispatch(setSpinnerDownloadEciForecast(showSpinner));
  }
);

export const spinnerDownloadFilledEciAppForm: ActionCreator<ThunkAction<void, RootState, unknown, Actions>> = (
  showSpinner: boolean
) => (
  async (dispatch) => {
    dispatch(setSpinnerDownloadFilledEciAppForm(showSpinner));
  }
);

export const spinnerUploadFilledEciAppForm: ActionCreator<ThunkAction<void, RootState, unknown, Actions>> = (
  showSpinner: boolean
) => (
  async (dispatch) => {
    dispatch(setSpinnerUploadFilledEciAppForm(showSpinner));
  }
);

// Reducers

const selectionNumber: Reducer<string> = (state = initialState.selectionNumber, action) => {
  switch (action.type) {
    case Type.SET_SELECTIONNUMBER:
      return (action as SetSelectionNumberAction).selectionNumber;
    default:
      return state;
  }
};

const season: Reducer<string> = (state = initialState.season, action) => {
  switch (action.type) {
    case Type.SET_SEASON:
      return (action as SetSeasonAction).season;
    default:
      return state;
  }
};

const supplierCode: Reducer<string> = (state = initialState.supplierCode, action) => {
  switch (action.type) {
    case Type.SET_SUPPLIERCODE:
      return (action as SetSupplierCodeAction).supplierCode;
    default:
      return state;
  }
};

const reference: Reducer<string> = (state = initialState.reference, action) => {
  switch (action.type) {
    case Type.SET_REFERENCE:
      return (action as SetReferenceAction).reference;
    default:
      return state;
  }
};

const style: Reducer<string> = (state = initialState.style, action) => {
  switch (action.type) {
    case Type.SET_STYLE:
      return (action as SetStyleAction).style;
    default:
      return state;
  }
};

const errors: Reducer<IErrors> = (state = initialState.errors, action) => {
  switch (action.type) {
    case Type.SET_ERRORS:
      return (action as SetErrorsAction).errors;
    default:
      return state;
  }
};

const downloadEciForecastFileSpinner: Reducer<boolean> = (state = initialState.downloadEciForecastFileSpinner, action) => {
  switch (action.type) {
    case Type.SET_SPINNERDOWNLOADECIFORECAST:
      return (action as SetSpinnerDownloadEciForecastAction).showSpinner;
    default:
      return state;
  }
};

const uploadFileSpinner: Reducer<boolean> = (state = initialState.uploadFileSpinner, action) => {
  switch (action.type) {
    case Type.SET_SPINNERUPLOADFILLEDECIAPPFORM:
      return (action as SetSpinnerUploadFilledEciAppFormAction).showSpinner;
    default:
      return state;
  }
};

const downloadFileSpinner: Reducer<boolean> = (state = initialState.downloadFileSpinner, action) => {
  switch (action.type) {
    case Type.SET_SPINNERDOWNLOADFILLEDECIAPPFORM:
      return (action as SetSpinnerDownloadFilledEciAppFormAction).showSpinner;
    default:
      return state;
  }
};

export const eciImplantDetailsStore = combineReducers({
  selectionNumber,
  season,
  supplierCode,
  reference,
  style,
  errors,
  downloadEciForecastFileSpinner,
  uploadFileSpinner,
  downloadFileSpinner
});
